import { useMemo } from 'react'

import type { PredictionEvent } from '@/services/prediction/types'

import type { GetTeamColorFn } from '../utils/color'
import { getContractFullName } from '../utils/contractLabels'
import { findFullPeriodSection } from '../utils/findFullPeriodSection'
import { getPlayerImage } from '../utils/getPlayerImage'
import { parseMatchEventTitle } from '../utils/matchEventTitle'
import { parseMatchEventContracts } from '../utils/parseMatchEventContracts'

export interface MatchTeamColor {
  primaryColor?: string
  secondaryColor?: string
  logoUrl?: string
}

/**
 * Adapted from cdc-web `src/hooks/business/useMatchData.ts`。
 *
 * 差异（均已决策）：
 * - 与 cdc-web 相同：`getTeamColor`（`useGetTeamColor` 的调色板解析结果，已含
 *   dark rescue）可选注入，颜色/logo 只取调色板结果，不回落 `contract.team.*`
 *   内联字段（WWW-5080 对齐）——无 team 的合约（F1 车手、FX strike）由调色板
 *   按 id 排序索引分配颜色；
 * - 去掉 scores/abbrev/fallbackText/chance 等 TwoWayCard 未消费的返回项
 *   （chance 直接在 OutcomeRow 从 contract 读；badgeText 在 TwoWayCard 里算；
 *   isNational 未消费，不返回）。
 */
export function useMatchData(event: PredictionEvent, getTeamColor?: GetTeamColorFn) {
  return useMemo(() => {
    const mlSection = findFullPeriodSection(event.sections ?? [], 'moneyline')
    const { leftContract, rightContract } = parseMatchEventContracts(mlSection?.contracts)

    const isPlayerMatch = !!(
      (leftContract?.individual_profile?.first_name &&
        leftContract?.individual_profile?.last_name) ||
      (rightContract?.individual_profile?.first_name &&
        rightContract?.individual_profile?.last_name)
    )

    const { titleLeft, titleRight } = parseMatchEventTitle(event.title)
    const name = [
      getContractFullName(leftContract, titleLeft),
      getContractFullName(rightContract, titleRight),
    ]

    // 只取调色板解析结果（与 cdc-web 一致）：不做 `contract.team.*` 兜底——
    // raw `team.color` 未经冲突消解/亮度钳制，icon_url 与 logo_url 可能不同
    // 配色（WWW-5080 差异根因）。调色板未命中时保持 undefined，走无颜色渲染。
    const leftTeamColor = getTeamColor?.(leftContract?.team?.id, leftContract?.id)
    const rightTeamColor = getTeamColor?.(rightContract?.team?.id, rightContract?.id)

    const color: MatchTeamColor[] = [
      {
        primaryColor: leftTeamColor?.color,
        secondaryColor: leftTeamColor?.secondaryColor,
        logoUrl: leftTeamColor?.logoUrl,
      },
      {
        primaryColor: rightTeamColor?.color,
        secondaryColor: rightTeamColor?.secondaryColor,
        logoUrl: rightTeamColor?.logoUrl,
      },
    ]

    const playerImages = [getPlayerImage(leftContract), getPlayerImage(rightContract)]

    return { name, color, isPlayerMatch, playerImages }
  }, [event, getTeamColor])
}
